/**
 * Playback sync queue
 *
 * Pure last-write-wins queue for the offline-first sync of chapter playback
 * state. Entries are shaped
 *   `{ file_id, chapter_id, playback_state, op, locally_modified_at }`
 * and `enqueue` collapses duplicates per `(file_id, chapter_id)` to the
 * newest write. Drain decisions compare UTC ISO-8601 timestamps
 * lexicographically — with a `Date.parse` fallback for non-strict shapes —
 * so the comparison is time-zone agnostic across devices (SC-009 / SC-010).
 *
 * The queue persists itself to `localStorage` under the caller-supplied key
 * (which the API provider namespaces per signed-in account, see
 * specs/006-sync-played-chapters/data-model.md R-3).
 */

const ENTRY_KEYS = ['file_id', 'chapter_id', 'playback_state', 'op', 'locally_modified_at'];
const ISO_UTC_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

function isWellFormedIsoUtc(value) {
  return typeof value === 'string' && ISO_UTC_RE.test(value);
}

function isValidEntry(entry) {
  if (!entry || typeof entry !== 'object') return false;
  for (const key of ENTRY_KEYS) {
    if (!(key in entry)) return false;
  }
  if (typeof entry.file_id !== 'string' || !entry.file_id) return false;
  if (typeof entry.chapter_id !== 'string' || !entry.chapter_id) return false;
  if (!Number.isInteger(entry.playback_state)) return false;
  if (entry.op !== 'upsert' && entry.op !== 'clear') return false;
  if (typeof entry.locally_modified_at !== 'string' || !entry.locally_modified_at) return false;
  return true;
}

function compareIsoUtc(a, b) {
  if (isWellFormedIsoUtc(a) && isWellFormedIsoUtc(b)) {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  }
  const da = Date.parse(a);
  const db = Date.parse(b);
  if (Number.isNaN(da) || Number.isNaN(db)) return 0;
  if (da < db) return -1;
  if (da > db) return 1;
  return 0;
}

export function createPlaybackSyncQueue({ storageKey } = {}) {
  if (!storageKey || typeof storageKey !== 'string') {
    throw new Error('createPlaybackSyncQueue: storageKey is required');
  }

  let entries = [];

  function indexOf(fileId, chapterId) {
    return entries.findIndex(
      (e) => e.file_id === fileId && e.chapter_id === chapterId,
    );
  }

  function persist() {
    try {
      localStorage.setItem(storageKey, JSON.stringify(entries));
    } catch (e) {
      console.warn('PlaybackSyncQueue: persist failed', e);
    }
  }

  function hydrate() {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        entries = [];
        return;
      }
      const parsed = JSON.parse(raw);
      entries = Array.isArray(parsed) ? parsed.filter(isValidEntry) : [];
    } catch (e) {
      console.warn('PlaybackSyncQueue: hydrate failed', e);
      entries = [];
    }
  }

  function enqueue(entry) {
    if (!isValidEntry(entry)) return;
    const idx = indexOf(entry.file_id, entry.chapter_id);
    const copy = { ...entry };
    if (idx >= 0) {
      entries[idx] = copy;
    } else {
      entries.push(copy);
    }
    persist();
  }

  function list() {
    return entries.map((e) => ({ ...e }));
  }

  function removeMatching(predicate) {
    if (typeof predicate !== 'function') return;
    const next = entries.filter((e) => !predicate(e));
    if (next.length !== entries.length) {
      entries = next;
      persist();
    }
  }

  function decideDrain(entry, lastSyncedAt) {
    if (!isValidEntry(entry)) return 'drop';
    if (lastSyncedAt == null) return 'push';
    if (typeof lastSyncedAt !== 'string') return 'push';
    return compareIsoUtc(entry.locally_modified_at, lastSyncedAt) > 0 ? 'push' : 'drop';
  }

  function clear() {
    if (entries.length === 0) return;
    entries = [];
    persist();
  }

  return { enqueue, list, removeMatching, decideDrain, persist, hydrate, clear };
}
